import React, { useState, useEffect, useRef, useCallback } from 'react';
import APIService from '../services/api';

// ── Système @mention partagé ─────────────────────────────────────────────────
// Un seul endroit pour taguer un collègue dans un texte libre (commentaires,
// actions, notes…) : autocomplete sur l'annuaire org (GET /users), regex
// Unicode (les usernames accentués « françois » ne sont pas coupés), rendu en
// pastilles, et notification in-app des personnes mentionnées à la
// publication (POST /notifications/inapp — clic = navigation via ref_type).

const BASE = 'https://besoins-tat-production.up.railway.app';
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });

// Annuaire org partagé (un seul fetch par session, quel que soit l'écran)
let teamCache = null;
let teamPromise = null;
export function useTeam() {
  const [team, setTeam] = useState(teamCache || []);
  useEffect(() => {
    if (teamCache) { setTeam(teamCache); return; }
    teamPromise = teamPromise || fetch(`${BASE}/users`, { headers: authH() })
      .then(r => (r.ok ? r.json() : {}))
      .then(j => { teamCache = (j.data || j.users || (Array.isArray(j) ? j : [])).filter(u => u.username); return teamCache; })
      .catch(() => []);
    let alive = true;
    teamPromise.then(t => { if (alive && Array.isArray(t)) setTeam(t); });
    return () => { alive = false; };
  }, []);
  return team;
}

// Interne = membre de l'équipe (recruteur, manager, admin…). Les comptes
// « ressource » (consultants en mission) ne sont PAS mentionnables : ils
// n'ont pas accès aux écrans internes où vivent ces discussions.
export const isInternal = (u) => !(u?.role === 'ressource' || u?.ressource === true);

export const escRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Utilisateurs effectivement mentionnés dans un texte (@username exact) —
// internes uniquement (taper @compte-ressource à la main ne notifie pas)
export const mentionedUsers = (text, team) =>
  (team || []).filter(u => isInternal(u) && new RegExp(`@${escRe(u.username)}(?![\\p{L}\\p{N}._-])`, 'iu').test(text || ''));

// Notifie chaque personne mentionnée (sauf l'auteur) — best-effort, jamais bloquant.
// Retourne le nombre de personnes notifiées.
export async function notifyMentions({ text, team, message, refType, refId }) {
  const me = (() => { try { return APIService.getCurrentUser(); } catch { return null; } })();
  const users = mentionedUsers(text, team).filter(u => u.id !== me?.id);
  await Promise.all(users.map(u =>
    fetch(`${BASE}/notifications/inapp`, {
      method: 'POST',
      headers: { ...authH(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: u.id, message, type: 'mention', ref_type: refType, ref_id: refId }),
    }).catch(() => {})
  ));
  return users.length;
}

// Rendu d'un texte avec les @mentions en pastilles
export function renderMentions(text) {
  if (!text) return null;
  const re = /@([\p{L}\p{N}._-]+)/gu;
  const out = []; let last = 0; let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<span key={m.index} style={{ background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)', padding: '1px 5px', borderRadius: '5px', fontWeight: 600 }}>@{m[1]}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Zone de texte avec autocomplete @ (self-contained : aucune classe CSS requise)
export function MentionArea({ value, onChange, team, rows = 2, placeholder, style, autoFocus, onFocus, onBlur }) {
  const [q, setQ] = useState(null); // null = pas de mention en cours
  const ref = useRef(null);

  const onInput = useCallback((e) => {
    const v = e.target.value;
    onChange(v);
    const before = v.slice(0, e.target.selectionStart);
    const mm = before.match(/@([\p{L}\p{N}._-]*)$/u);
    setQ(mm ? mm[1].toLowerCase() : null);
  }, [onChange]);

  const insert = (u) => {
    const el = ref.current; const pos = el ? el.selectionStart : (value || '').length;
    const before = (value || '').slice(0, pos).replace(/@([\p{L}\p{N}._-]*)$/u, `@${u.username} `);
    onChange(before + (value || '').slice(pos));
    setQ(null); el && el.focus();
  };

  const filtered = q != null
    ? (team || []).filter(u => isInternal(u) && (u.username.toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q))).slice(0, 6)
    : [];

  return (
    <div style={{ position: 'relative' }}>
      <textarea ref={ref} value={value} onChange={onInput} rows={rows} autoFocus={autoFocus}
        placeholder={placeholder || 'Tapez @ pour mentionner un collègue…'}
        onFocus={onFocus}
        onBlur={() => { setTimeout(() => setQ(null), 150); onBlur?.(); }}
        style={{ boxSizing: 'border-box', width: '100%', border: '1px solid var(--input-border)', borderRadius: '8px', background: 'var(--input-bg)', padding: '8px 10px', fontFamily: 'inherit', fontSize: '12.5px', color: 'var(--text-strong)', outline: 'none', resize: 'vertical', ...style }} />
      {filtered.length > 0 && (
        <div style={{ position: 'absolute', left: 0, bottom: '100%', marginBottom: '6px', width: '230px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: '0 8px 28px var(--hover-shadow)', overflow: 'hidden', zIndex: 80 }}>
          {filtered.map(u => (
            <button key={u.id} onMouseDown={e => { e.preventDefault(); insert(u); }}
              style={{ width: '100%', textAlign: 'left', padding: '8px 11px', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text)', fontFamily: 'inherit' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--ghost-bg-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
              <span style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'var(--accent-soft-bg)', color: 'var(--text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600, fontFamily: "'IBM Plex Mono',monospace", flex: 'none' }}>
                {(u.username || '?')[0].toUpperCase()}
              </span>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.username}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
